import { apiRequest } from '../../../services/api/client';
import type { AccessibleApplication, AccessibleChurch, AuthUser } from '../types/auth.types';

type MeResponse = {
  data: {
    id: number;
    name: string;
    email: string;
    member_id: number | null;
    is_super_admin: boolean;
    permission_codes: string[];
  };
};

type ChurchesResponse = {
  current_church_id: number | null;
  data: Array<{
    id: number;
    name: string;
    parent_id: number | null;
    level: {
      id: number;
      name: string;
      order: number;
    };
  }>;
};

type ApplicationsResponse = {
  data: Array<{
    id: number;
    name: string;
    slug: string | null;
    launch_url: string | null;
  }>;
};

const toUser = (user: MeResponse['data']): AuthUser => ({
  id: String(user.id),
  name: user.name,
  email: user.email,
  memberId: user.member_id ? String(user.member_id) : undefined,
  isSuperAdmin: user.is_super_admin,
  permissionCodes: user.permission_codes ?? [],
});

const toChurch = (church: ChurchesResponse['data'][number]): AccessibleChurch => ({
  id: String(church.id),
  name: church.name,
  parentId: church.parent_id ? String(church.parent_id) : undefined,
  level: {
    id: String(church.level.id),
    name: church.level.name,
    order: church.level.order,
  },
});

const toApplication = (application: ApplicationsResponse['data'][number]): AccessibleApplication | null => {
  if (!application.launch_url) return null;

  return {
    id: String(application.id),
    name: application.name,
    slug: application.slug,
    launchUrl: application.launch_url,
  };
};

export const authService = {
  async me() {
    const payload = await apiRequest<MeResponse>('/auth/me');
    return toUser(payload.data);
  },

  async churches() {
    const payload = await apiRequest<ChurchesResponse>('/user/churches');

    return {
      currentChurchId: payload.current_church_id ? String(payload.current_church_id) : null,
      churches: payload.data.map(toChurch),
    };
  },

  async applications() {
    const payload = await apiRequest<ApplicationsResponse>('/user/applications');

    return payload.data
      .map(toApplication)
      .filter((application): application is AccessibleApplication => Boolean(application));
  },

  async logout() {
    await apiRequest<void>('/auth/logout', { method: 'POST' });
  },
};
